<template>
  <div>
    <Head>
      <Title>{{ pageTitle }} : ShareInvestor Thailand</Title>
    </Head>

    <div v-if="isLoading">
      <HelpersSpinner />
    </div>
    <div v-else>
      <h1 class="visually-hidden">Intro Page</h1>
      <div class="intro-page-content" v-html="pageContent"></div>
    </div>
  </div>
</template>

<script setup>

definePageMeta({
  layout: "intro-page",
});

import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useBreadcrumbSchema } from '~/composables/useBreadcrumbSchema';

const { baseApi } = useApiBase();
const route = useRoute();
const { public: { siteUrl } } = useRuntimeConfig();

const pageTitle = ref('');
const pageContent = ref('');
const isLoading = ref(true);
const customHead = ref(null);
const customFooter = ref(null);

const loadScript = (src, isAsync = true, isDefer = true) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = isAsync;
    script.defer = isDefer;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
};

const injectCustomHead = (customHead) => {
  const head = document.head;
  const headFragment = document.createRange().createContextualFragment(customHead);
  head.appendChild(headFragment);
};

const injectCustomFooter = async (customFooter) => {
  const body = document.body;
  const footerFragment = document.createRange().createContextualFragment(customFooter);

  // ดึง script จาก customFooter
  const scripts = footerFragment.querySelectorAll('script[src]');
  const scriptUrls = Array.from(scripts).map((script) => script.src);

  // Append footer HTML ยกเว้น script
  scripts.forEach((script) => script.remove());
  body.appendChild(footerFragment);

  // โหลด script ทีละตัว
  for (const src of scriptUrls) {
    try {
      await loadScript(src);
    } catch (error) {
      console.error(`Failed to load script: ${src}`, error);
    }
  }
};

// SSR-safe fetch, awaited at setup — previously this only ran inside
// onMounted, so neither <title> nor the H1/body content was ever present
// in the server-rendered HTML (this was the one page in the app with no
// SSR content at all).
try {
  const response = await $fetch(`${baseApi}/en/intro-page`);
  const pageData = response.data;
  pageTitle.value = pageData.title;
  pageContent.value = pageData.body;
  customHead.value = pageData.custom_head || null;
  customFooter.value = pageData.custom_footer || null;
} catch (error) {
  console.error('Error loading intro page content:', error);
  pageTitle.value = 'Error';
  pageContent.value = '<p>An error occurred while loading the content.</p>';
  await navigateTo('/en/home');
}

isLoading.value = false;

// No usable CMS title/breadcrumbs for this route (title is the generic
// brand default, breadcrumbs is null) so the name matches the visually
// hidden H1 above instead.
const pageUrl = computed(() => `${siteUrl}${route.path}`);

useHead(() => ({
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(useBreadcrumbSchema([
        { name: "Home", url: `${siteUrl}/` },
        { name: "Intro Page", url: pageUrl.value },
      ], pageUrl.value)),
    },
  ],
}));

// DOM injection touches document/script tags directly, so it must stay
// client-only — runs once the page data above has already been fetched.
onMounted(() => {
  if (customHead.value) injectCustomHead(customHead.value);
  if (customFooter.value) injectCustomFooter(customFooter.value);
});

</script>

<style scoped>
/* Add custom styles for your intro page here */

/* This page's layout (layouts/IntroPage.vue) doesn't import the global
   fontawesome CSS that provides `.sr-only` elsewhere in the app, so the
   same visually-hidden technique is defined locally here for the H1 above. */
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>